import { useCallback, useEffect, useState } from "react";

import { GridLayout } from "@newsela/angelou";

import { ContentTypeEnum } from "@/components/ContentCard/ContentCard.interface";
import ContentCardV2 from "@/components/ContentCardV2";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import contentTypes from "@/view/constants/contentTypes";
import { UNLEVELLED_CONTENT_TYPES } from "@/view/constants/contentTypes/contentTypes.constants";
import uiTypes from "@/view/constants/uiTypes";
import { Metadata } from "@/view/ContentHead";
import findSensitiveContentTag from "@/view/utils/findSensitiveContentTag";
import shuffleRelatedContentCards from "@/view/utils/shuffleRelatedContentCards";
import useFetchRelatedContentCards from "@/view/utils/useFetchRelatedContentCards";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import { IContent } from "@/view/utils/useSetInitialLevel";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import { gridCss, relatedContentTitle, rootCss } from "./RelatedContent.style";
import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

interface RelatedContentProps {
  content: IContent | IUnlevelledContent;
  contentMetadata: Metadata[];
  legacyArticleLevel?: {
    content_id: string;
    header_id: string;
    id: number;
    slug: string;
  };
}

const RelatedContent = ({
  content,
  contentMetadata,
  legacyArticleLevel,
}: RelatedContentProps) => {
  const userProperties = useGetUserProperties();
  const { uiType } = useUserContentUIContext();
  const { isStudent, canViewSearch } = userProperties;
  const contentIsVideo =
    content.content_type === contentTypes.INTERACTIVE_VIDEO;
  const contentIsUnlevelled = UNLEVELLED_CONTENT_TYPES.includes(
    content.content_type,
  );
  const contentId =
    contentIsVideo || contentIsUnlevelled
      ? content.id
      : legacyArticleLevel?.header_id;
  const { fetchRelatedContentCards, filteredCards: relatedContentCards } =
    useFetchRelatedContentCards({
      contentId: content.id,
      isStudent,
      legacyFormat: true,
    });
  const { isVideoPresentModeActive } = usePresentModeContext();
  // State is required for shuffled cards since relatedContentCards is empty on first render
  const [shuffledCards, setShuffledCards] = useState<any[]>();
  const liteUserLiteContent = uiType === uiTypes.LITE;
  const hideRelatedContentForFreeStudentUserInTrial =
    liteUserLiteContent && isStudent;

  const renderCard = useCallback((card: any) => {
    const contentType =
      card.content_type.toLowerCase() === "articleheader"
        ? ContentTypeEnum.ARTICLE
        : card.content_type.toUpperCase();

    return (
      <ContentCardV2
        key={card.content_id}
        id={card.content_id}
        description={card.description}
        display_date={card.date_published}
        images={card.images}
        metadata={{
          content_type: contentType,
          date_lite_product_grant_expires:
            card?.date_lite_product_grant_expires,
          lexile_levels: card?.lexile_levels,
          maturity_range: content.data.maturity_range,
        }}
        title={card.title}
        url={card.url}
        viewmodel_type="card"
      />
    );
  }, []);

  useEffect(() => {
    fetchRelatedContentCards();
  }, []);

  useEffect(() => {
    setShuffledCards(shuffleRelatedContentCards(relatedContentCards));
  }, [relatedContentCards.length]);

  const isContentSensitive =
    contentMetadata && findSensitiveContentTag(contentMetadata);

  const shouldNotDisplayRelatedContent =
    !contentMetadata ||
    isContentSensitive ||
    !contentId ||
    !shuffledCards?.length ||
    isVideoPresentModeActive ||
    hideRelatedContentForFreeStudentUserInTrial ||
    !canViewSearch;

  if (shouldNotDisplayRelatedContent) {
    return null;
  }

  return (
    <div css={rootCss}>
      <h2 css={relatedContentTitle}>Related content</h2>
      <GridLayout minChildWidth="200px" __cssFor={{ root: gridCss }}>
        {/* @ts-ignore TODO: convert BookmarkContextProvider to Typescript */}
        <BookmarkContextProvider>
          {shuffledCards?.map(renderCard)}
        </BookmarkContextProvider>
      </GridLayout>
    </div>
  );
};

export default RelatedContent;
